import React, { useState, useEffect, useCallback, useRef } from 'react';
//import DatePicker from 'react-datepicker'; // 引入 react-datepicker
//import "react-datepicker/dist/react-datepicker.css"; // 引入样式

import { Howl } from 'howler';

import { AppProvider, useAppContext } from './context/AppContext'; // 导入 AppContext 提供器

import './App.css';
import LogMonitor from './components/LogMonitor';
import Login from './components/login';
import Register from './components/register'; // 新增注册组件的引用
import TradingUnitStreamContainer from './components/TradingUnitStreamContainer';

import { ReliableFetch, isIOSBrowser, setInMemoryAccessToken } from './utils/reliablefetch.ts';  // ✅ 别名导入（推荐）
import { NoSleepIOS } from './utils/nosleep.ts';

import { t } from './i18n/i18n'; // 根据实际文件路径调整

//////////////////////////////////////////////////////////////////////////////
//                                                                          //
//                                   配置区                                  //
//                                                                          //
//////////////////////////////////////////////////////////////////////////////
const RETRYTIMES = 10


//////////////////////////////////////////////////////////////////////////////
//                                                                          //
//                                   业务区                                  //
//                                                                          //
//////////////////////////////////////////////////////////////////////////////
// revision及orderline等的修改都不会直接提交到后端。
// 而在改变状态才会调用这个，但是改为pending了才允许改变改变状态，而pending靠的是PendingUserRevision执行。RevsionCard组件里更新其他状态用了下
// 修改TradingUnit基本信息也需要修改其head revision的init信息

export const useSound = (soundFile) => {
    const [sound, setSound] = useState(null);
    const soundRef = useRef(null);

    useEffect(() => {
        const newSound = new Howl({
            src: [soundFile],
            onend: () => {
                // 播放结束时触发的回调
            },
        });
        setSound(newSound);
        soundRef.current = newSound;
        return () => {
            if (soundRef.current) {
                soundRef.current.unload();
            }
        };
    }, [soundFile]);

    const play = useCallback(() => {// 使用useCallback保持稳定，其实也可以不写它们的依赖，若不动态变声音文件的话
        if (soundRef.current) {
            soundRef.current.play();
        }
    }, []);  // 依赖为空，soundRef.current 本身不会变化

    const playMultipleTimes = useCallback((times) => {// 使用useCallback保持稳定，其实也可以不写它们的依赖，若不动态变声音文件的话
        if (soundRef.current && times > 0) {
            const playNext = (remaining) => {
                if (remaining > 0) {
                    soundRef.current.once('end', () => {
                        playNext(remaining - 1);
                    });
                    soundRef.current.play();
                }
            };
            playNext(times);
        }
    }, []);

    return [play, playMultipleTimes];
};

export async function beUpdateUserRevision(URLPrefix, newRevision, IsUpdateTimestamp = false) {//ATTENSION：由于是异步的关系，所以此函数一定要无状态，否则会出现tradingUnits的逻辑不写在react的setState的函数式编程里就会有问题的情况。
    try {
        const response = await ReliableFetch(`${URLPrefix}/user/tr/update`, {
            method: 'PUT',
            credentials: 'include', // 带cookie的关键！
            headers: { 'Content-Type': 'application/json' },
            //body: JSON.stringify(newRevision), // 直接发送 newRevision 对象
            body: JSON.stringify({
                revision: newRevision,
                isUpdateTimestamp: IsUpdateTimestamp
            }),
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error(`[beUpdateUserRevision]HTTP error ${response.status}: ${errorText}`);
            return false;
        }
        try {
            const data = await response.json();
            console.info('[beUpdateUserRevision]User revision updated successfully:', data);
            return true;
        } catch (error) {
            console.error(`[beUpdateUserRevision]response.json() failed: ${error}`);
            return false;
        }
    } catch (error) {
        console.error('[beUpdateUserRevision]Error updating user revision:', error);
        return false;
    }
}

//暂时同updateUserRevisiony一样传入的都是完整数据，意味着顺便副作用也能修改revision
export async function bePendUserRevision(URLPrefix, Rev) {//ATTENSION：由于是异步的关系，所以此函数一定要无状态，否则会出现tradingUnits的逻辑不写在react的setState的函数式编程里就会有问题的情况。
    // //暂时只支持初始开仓(tradingunit)Revision。TODO：其实还应该判断_has_entry_point_order
    // if ( !('init' in Rev) ) {
    //     console.warn("[PostRevision]暂时不支持修订");
    //     return false;
    // }
    try {
        //delete Rev["rawMsg"];//没必要的传来传去了。而且可能含有/n会导致后端json.loads失败——不过奇怪的是后端传给前端的时候回车是怎么处理的呢？如果删除的属性不存在，delete 不会报错，而是返回 true
        const postdata = {
            method: "POST",
            credentials: 'include', // 带cookie的关键！                    
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(Rev), // 直接发送 newRevision 对象
        };
        //console.info(postdata)
        const response = await ReliableFetch(`${URLPrefix}/user/tr/pending`, postdata);
        if (!response.ok) {
            const errorText = await response.text(); // 获取错误信息文本
            console.error(`[bePendUserRevision]HTTP error ${response.status}: ${errorText}`);
            //throw new Error(`HTTP error ${response.status}: ${errorText}`); // 抛出错误，以便调用者处理
            return false;
        }
        try {
            const data = await response.json();
            console.info("[bePendUserRevision]Response received:", data);
            return true;
        } catch (error) {
            console.error(`[bePendUserRevision]response.json() failed: ${error}`);
            return false;
        }
    } catch (error) {
        console.error("[bePendUserRevision]Error sending trading unit:", error);
        return false;
    }
}
//PostRevision({}


//////////////////////////////////////////////////////////////////////////////
//                                                                          //
//                                   主界面                                  //
//                                                                          //
//////////////////////////////////////////////////////////////////////////////
const App = () => {
    return (
        <AppProvider> {/* 包裹应用组件 */}
            <MainApp />
        </AppProvider>
    );
};

const MainApp = () => {
    // 屏幕常亮功能
    const wakeLockRef = useRef(null);
    const noSleepRef = useRef(null);
    const noSleepEnabledRef = useRef(false);
    const noSleepArmedRef = useRef(false);

    const requestWakeLock = useCallback(async () => {
        try {
            if ('wakeLock' in navigator) {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
                console.log('屏幕常亮已启用');
                return;
            }
            // iOS Safari/Firefox/Chrome 当前不支持 Wake Lock API，使用 NoSleep.js 的 video 技巧做兜底（需用户首次手势触发）。
            // FIXME(iOS-wakelock): 等 iOS 原生支持后删除该 fallback。
            if (isIOSBrowser()) {
                if (!noSleepRef.current) noSleepRef.current = new NoSleepIOS();
                if (!noSleepEnabledRef.current && !noSleepArmedRef.current) {
                    noSleepArmedRef.current = true;
                    const enableOnce = async () => {
                        try {
                            await noSleepRef.current.enable();
                            noSleepEnabledRef.current = true;
                            console.log('iOS NoSleep fallback enabled');
                        } catch (err) {
                            console.warn('iOS NoSleep fallback failed:', err);
                        } finally {
                            noSleepArmedRef.current = false;
                        }
                    };
                    // 必须由用户手势触发；使用 once 以避免常驻监听。
                    window.addEventListener('touchstart', enableOnce, { once: true, passive: true });
                    window.addEventListener('touchend', enableOnce, { once: true, passive: true });
                    window.addEventListener('click', enableOnce, { once: true });
                }
            }
        } catch (err) {
            console.warn('无法启用屏幕常亮:', err);
        }
    }, []);

    const releaseWakeLock = useCallback(() => {
        if (wakeLockRef.current) {
            wakeLockRef.current.release();
            wakeLockRef.current = null;
            console.log('屏幕常亮已关闭');
        }
        if (noSleepRef.current) {
            noSleepRef.current.disable();
            noSleepEnabledRef.current = false;
        }
    }, []);

    // 在所有设备上尝试启用屏幕常亮
    useEffect(() => {
        requestWakeLock();

        // 监听页面可见性变化，重新请求wake lock
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
                if (noSleepEnabledRef.current && noSleepRef.current) {
                    // 重新回到前台后尝试恢复播放（可能会被 iOS 暂停）。
                    noSleepRef.current.enable().catch(() => {
                        // iOS 常常要求再次手势触发播放；标记为未启用，以便 requestWakeLock 重新挂载一次性监听。
                        noSleepEnabledRef.current = false;
                        requestWakeLock();
                    });
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            releaseWakeLock();
        };
    }, [requestWakeLock, releaseWakeLock]);

    const forceLandscape = () => {
        const isMobile = /Mobi|Android/i.test(navigator.userAgent);
        const isPortrait = window.innerHeight > window.innerWidth;
        if (isMobile && isPortrait) {
            const viewport = document.querySelector('meta[name="viewport"]');
            viewport.setAttribute('content', 'width=device-height, initial-scale=1.0');

            const appContainer = document.getElementById('root'); // 修改为你的根容器
            appContainer.style.transform = "rotate(90deg)";
            appContainer.style.transformOrigin = "center center";
            appContainer.style.width = "100vh";
            appContainer.style.height = "100vw";
            //appContainer.style.position = "fixed";
            appContainer.style.position = "absolute";
            appContainer.style.top = "50%";
            appContainer.style.left = "50%";
            appContainer.style.margin = "0";
            appContainer.style.transform = "translate(-50%, -50%) rotate(90deg)";
            // 强制计算安全区域的内边距
            const safePadding = {
                top: window.innerHeight * 0.05, // 假设 5% 为安全区域
                left: window.innerWidth * 0.05, // 假设 5% 为安全区域
            };
            appContainer.style.paddingTop = `${safePadding.top}px`;
            appContainer.style.paddingLeft = `${safePadding.left}px`;
            appContainer.style.paddingRight = `${safePadding.left}px`;
            appContainer.style.paddingBottom = `${safePadding.top}px`;
        } else {
            const viewport = document.querySelector('meta[name="viewport"]');
            viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
            const appContainer = document.getElementById('root');
            appContainer.style.transform = "";
            appContainer.style.width = "100%";
            appContainer.style.height = "auto";
            appContainer.style.position = "static";
            appContainer.style.top = "";
            appContainer.style.left = "";
            appContainer.style.margin = "0";
            appContainer.style.paddingTop = ""; // 清除内边距
            appContainer.style.paddingLeft = ""; // 清除左侧内边距
            appContainer.style.paddingRight = ""; // 清除右侧内边距
            appContainer.style.paddingBottom = ""; // 清除底部内边距
        }
    };
    // useEffect(() => {
    //     // 监听设备方向变化
    //     window.addEventListener("resize", forceLandscape);
    //     window.addEventListener("orientationchange", forceLandscape);
    //     // 初次加载时判断方向
    //     forceLandscape(); 暂时关闭强制横屏，因为手机端的横屏左边文字和控件会被手机屏幕上面的状态栏遮挡
    //     return () => {
    //         window.removeEventListener("resize", forceLandscape);
    //         window.removeEventListener("orientationchange", forceLandscape);
    //     };
    // }, []);

    //========================================================================
    const {/*setIsStatusModalOpen, setIsRawMsgModalOpen,  setIsDeleteModalOpen, */
        localTimezone, setLocalTimezone,
        ServiceURLPrefix,
        ServerName,
        username,
        message, setMessage,
        logWSRetries,
        isBlocking, blockPage, unblockPage,
    } = useAppContext();

    //========================================================================
    const [playSound, playSoundMultiple] = useSound('/goldcoin.mp3');
    const isPlayingRef = useRef(false); // 使用 useRef 存储播放状态
    const playCountRef = useRef(0); // 使用 useRef 存储播放次数
    const intervalRef = useRef(null); // 使用 useRef 存储定时器 ID
    const timeoutRef = useRef(null); // 使用 useRef 存储超时 ID

    const startPlayingSoundMultiple = useCallback((times = 3) => {
        if (isPlayingRef.current)
            return; // 如果已经在播放，则不再启动
        isPlayingRef.current = true;
        playCountRef.current = 0;
        // 每6秒播放一次音效
        intervalRef.current = setInterval(() => {
            if (playCountRef.current < 300) { // 最多播放300次
                playSoundMultiple(times);
                playCountRef.current += 1;
            } else {
                clearInterval(intervalRef.current);
                isPlayingRef.current = false;
            }
        }, 6000);
        // 停止播放
        timeoutRef.current = setTimeout(() => {
            clearInterval(intervalRef.current);
            isPlayingRef.current = false;
        }, 600000); //单位毫秒10分钟
    }, [playSoundMultiple]);
    const stopPlaying = () => {
        clearInterval(intervalRef.current);
        clearTimeout(timeoutRef.current);
        isPlayingRef.current = false;
    };

    // 监听用户活动事件，停止播放
    useEffect(() => {
        const handleUserActivity = () => {
            if (isPlayingRef.current) {
                stopPlaying();
                console.debug("[handleUserActivity]Stop playing notice.");
            }
        };
        // 监听鼠标和键盘事件
        window.addEventListener('mousemove', handleUserActivity);
        window.addEventListener('keydown', handleUserActivity);
        window.addEventListener('click', handleUserActivity);
        window.addEventListener('touchstart', handleUserActivity);
        return () => {
            window.removeEventListener('mousemove', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            window.removeEventListener('click', handleUserActivity);
            window.removeEventListener('touchstart', handleUserActivity);
        };
    }, []);

    //========================================================================
    //const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token') || null); localStorage很不安全，会在调试模式或者用js看得到。
    const [isLoggedIn, setIsLoggedIn] = useState(null);// 登录状态：null 表示未检测，false 未登录，true 已登录
    const isLoggedInRef = useRef(isLoggedIn);
    //========================================================================
    // FIXME(iOS-auth): iOS 设备资源更紧张，默认全量拉取并渲染过多 revision 容易导致页面被系统回收（表现为“像刷新了一次”）。
    // 终极方案应做分页/增量拉取/列表虚拟化；此处先做最小兜底：iOS 下默认更小的条数 + 更慢的轮询。
    const IS_IOS = isIOSBrowser();
    const DEFAULT_UNIT_COUNT = IS_IOS ? 30 : 400;
    const FETCH_TRADING_UNITS_INTERVAL_MS = IS_IOS ? 15000 : 5000;
    // iOS 用更保守的预设，避免再次触发 WebKit 回收；Android/桌面保持原来的大数预设。
    const UNIT_COUNT_PRESETS = IS_IOS ? [30, 65, 100] : [300, 400, 500];

    const [unitCount, setUnitCount] = useState(DEFAULT_UNIT_COUNT);
    const unitCountRef = useRef(unitCount);//频繁变化的值，得在使用它的内部通过unitCountRef.current取值（本质应该就是个指针），而不能作为依赖，否则会频繁触发useEffect。
    //========================================================================
    // 定时任务：从后端拉取一批交易信号
    //FIXME：这个值会被子组件，乃至孙组件修改，理论上有风险，但对于我们这个简单程序暂时先这么做。
    const [tradingUnits, setTradingUnits] = useState([]);

    //========================================================================
    // 离线了就持续播放音效，直到监听到用户操作活动事件
    useEffect(() => {
        if (isLoggedIn === false) {
            // 未登录状态下，持续播放提醒音效
            startPlayingSoundMultiple();
        } else {
            // 登录后，停止提醒音效
            stopPlaying();
        }
    }, [isLoggedIn, startPlayingSoundMultiple]);

    //========================================================================
    // authMode 用于控制当前展示的是登录界面还是注册界面，初始为 "login"
    const [authMode, setAuthMode] = useState("login");

    const [logoutReason, setLogoutReason] = useState("");

    const recordLogoutReason = useCallback((reason) => {
        // FIXME(iOS-auth): 仅用于线上定位“iOS 登录后被打回登录页”的根因，确认后应移除或降级为 debug 日志。
        if (!IS_IOS) return;
        setLogoutReason(`被动退出原因: ${reason}`);
        try {
            sessionStorage.setItem("last_logout_reason", `${Date.now()}: ${reason}`);
        } catch {
            // ignore
        }
    }, [IS_IOS]);

    const checkTokenIntervalRef = useRef(null); // 用于存储 Token 检查的定时器。Ref方式可以保证内部取值，而不影响依赖渲染
    const fetchTradingUnitsIntervalRef = useRef(null); // 用于存储交易数据拉取的定时器。Ref方式可以保证内部取值，而不影响依赖渲染
    const clearUserData = useCallback((removeAuthToken = true) => {
        console.info("[clearUserData] Clearing user-specific data...");
        // 清空交易数据
        setTradingUnits([]);
        // 清空用户相关状态
        setMessage("");
        setUnitCount(DEFAULT_UNIT_COUNT);
        unitCountRef.current = DEFAULT_UNIT_COUNT;//与UnitCount同步更新
        // // 关闭所有 Modal，获取 modal 控制
        // setIsStatusModalOpen(false);
        // setIsRawMsgModalOpen(false);
        // setIsDeleteModalOpen(false);
        if (removeAuthToken) {
            localStorage.removeItem("access_token");
            sessionStorage.removeItem("access_token");
        }
        // setSearchQuery("");
        // setSortOption("default");

        clearInterval(checkTokenIntervalRef.current);
        clearInterval(fetchTradingUnitsIntervalRef.current);
        logWSRetries.current = 0;
    }, [setTradingUnits, setMessage, setUnitCount, logWSRetries, DEFAULT_UNIT_COUNT]);//useState出来的setter性函数都是稳定的，不怕放在依赖里

    // 登录成功的回调函数
    const handleLogin = (msg) => {
        setIsLoggedIn(true);
        isLoggedInRef.current = true;//与setStatexxxx同步更新
        // FIXME(iOS-auth): iOS 跨站 Cookie 可能被拦截，临时使用 localStorage token 兜底；非 iOS 保持纯 HttpOnly Cookie，更安全。
        clearUserData(!IS_IOS);
        //console.info(`[handleLogin]msg: ${msg}`);
        setMessage(`[INFO:handleLogin]msg: ${msg}`);
    };

    // 注册成功的回调函数
    const handleRegister = (msg) => {
        setIsLoggedIn(true);
        isLoggedInRef.current = true;//与setStatexxxx同步更新
        // FIXME(iOS-auth): iOS 跨站 Cookie 可能被拦截，临时使用 localStorage token 兜底；非 iOS 保持纯 HttpOnly Cookie，更安全。
        clearUserData(!IS_IOS);
        console.info(`[handleRegister]msg: ${msg}`);
        setMessage(`[INFO:handleRegister]response: ${msg}`);
    };

    const handleLogout = useCallback(async () => {
        const controller = new AbortController(); // 创建中断控制器
        const timeoutId = setTimeout(() => {
            controller.abort(); // 5秒后中断请求
        }, 5000);
        try {
            playSound();
            blockPage();
            const response = await ReliableFetch(`${ServiceURLPrefix}/user/logout`, { //  登出接口，后端实现
                method: 'POST',
                credentials: 'include', // 带cookie的关键！                    
                signal: controller.signal, // 绑定中断信号
            });
            clearTimeout(timeoutId); // 清除计时器

            const data = await response.json();
            if (response.ok) {
                console.info(`[handleLogout]response: ${data.message}`);
                setMessage(`[INFO:handleLogout]response: ${data.message}`);
            } else {
                console.error(`[handleLogout]response: ${data.message}`);
                setMessage(`[ERROR:handleLogout]response: ${data.message}`);
            }
        } catch (error) {
            clearTimeout(timeoutId); // 确保总是清除计时器

            // 处理超时错误
            if (error.name === 'AbortError') {
                console.warn("[handleLogout]登出请求超时");
                setMessage("[WARN:handleLogout]登出请求超时，请检查网络连接");
            }
            // 处理其他错误
            else {
                console.error(`[FATAL:handleLogout]${error}`);
                setMessage(`[FATAL:handleLogout]${error}`);
            }
        } finally {
            // 无论成功失败都执行清理
            setIsLoggedIn(false);
            isLoggedInRef.current = false;//与setStatexxxx同步更新
            setAuthMode("login");
            setInMemoryAccessToken(null);
            clearUserData();  // 调用清理函数
            // 清除所有定时器
            if (checkTokenIntervalRef.current) {
                clearInterval(checkTokenIntervalRef.current);
            }
            if (fetchTradingUnitsIntervalRef.current) {
                clearInterval(fetchTradingUnitsIntervalRef.current);
            }
            unblockPage();
        }
    }, [ServiceURLPrefix, clearUserData, setMessage, blockPage, unblockPage, playSound]);//useState范围的setter函数都是稳定的，不怕放在依赖里

    //========================================================================
    //增加 Token 刷新机制
    const beRefreshToken = useCallback(async (retries = RETRYTIMES) => {
        try {
            const response = await ReliableFetch(`${ServiceURLPrefix}/auth/refresh`, {
                method: 'POST',
                credentials: 'include', // 确保带上 Cookie
                headers: { ...(isIOSBrowser() ? { 'X-Return-Token': '1' } : {}) },
            });
            const data = await response.json();
            if (response.ok) {
                if (isIOSBrowser() && data.access_token) {
                    setInMemoryAccessToken(data.access_token);
                    try {
                        localStorage.setItem("access_token", data.access_token);
                    } catch {
                        // ignore storage errors (e.g. iOS private mode / quota)
                    }
                }
                console.log("Token refreshed successfully:", data.message);
                return true;
            } else {
                console.error("Failed to refresh token:", data.message);
                if (retries > 0) {
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    return beRefreshToken(retries - 1);
                }
                return false;
            }
        } catch (error) {
            console.error("Error refreshing token:", error);
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 5000));
                return beRefreshToken(retries - 1);
            }
            return false;
        }
    }, [ServiceURLPrefix]);
    const beCheckTokenExpiration = useCallback(async (retries = RETRYTIMES) => {
        if (!isLoggedInRef.current)//必须用新值，但没必要使用isLoggedIn作为依赖而导致本useCallback套函数重建，反正current值会与isLoggedIn同步更新。
            return false; // 只在已登录状态下检测    
        try {
            const response = await ReliableFetch(`${ServiceURLPrefix}/auth/expiration`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                console.warn("[beCheckTokenExpiration] Failed to check token expiration");
                // === 特殊处理 401 和 403 ===
                if (response.status === 401 || response.status === 403) {//这两种错误没必要重试
                    console.error("[beCheckTokenExpiration] Token expired, no need to retry. Logout...");
                    recordLogoutReason(`beCheckTokenExpiration ${response.status}`);
                    handleLogout();
                    return false;
                }
                // 其他错误（如 500/502/503），重试
                if (retries > 0) {
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    return beCheckTokenExpiration(retries - 1);
                }
                return false;
            }
            const data = await response.json();
            const remainingSeconds = data.remaining_seconds; // 后端返回的剩余时间（单位：秒）
            if (remainingSeconds <= 0) {
                console.error("[beCheckTokenExpiration] Token has expired. Logging out...");
                handleLogout();
                return false;
            } else if (remainingSeconds < 300) {
                console.warn("[beCheckTokenExpiration] Token is expiring soon. Attempting to refresh...");
                const refreshSuccess = await beRefreshToken();
                if (refreshSuccess)
                    return true; // 成功刷新，返回 true
                if (retries > 0) {
                    console.warn(`[beCheckTokenExpiration] Token refresh failed. Retrying... (${retries} attempts left)`);
                    await new Promise(resolve => setTimeout(resolve, 5000)); // 5 秒后重试
                    return beCheckTokenExpiration(retries - 1);
                }
                console.error("[beCheckTokenExpiration] All token refresh attempts failed, logging out...");
                handleLogout();
                return false;
            }
            return true; // Token 仍然有效，返回 true

        } catch (error) {
            console.error("[beCheckTokenExpiration] Error checking token expiration:", error);
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 5000));
                return beCheckTokenExpiration(retries - 1);
            }
            return false;
        }
    }, [ServiceURLPrefix, beRefreshToken, handleLogout, recordLogoutReason]);

    useEffect(() => {
        if (isLoggedIn) {
            console.info("[useEffect] Setting up token expiration check...");
            checkTokenIntervalRef.current = setInterval(beCheckTokenExpiration, 30 * 1000); // 每 60 秒检查一次
            return () => {
                if (checkTokenIntervalRef.current) clearInterval(checkTokenIntervalRef.current);
            };
        }
    }, [isLoggedIn, beCheckTokenExpiration]);

    //========================================================================
    const [forceRenderCounter, setForceRenderCounter] = useState(0);    // 新增：用于强制刷新组件
    const forceChildRender = useCallback(() => {// 对外暴露触发函数
        setForceRenderCounter(prev => prev + 1);
    }, []);
    //========================================================================
    // 保留原有 offset 机制，始终传 offset=-1；同时增加 latestRevisionId 参数支持
    // latestRevisionId 用于只拉取新增数据，当 tradingUnits 数量不足 unitCount 时，则全量拉取（传 latestRevisionId=-1）
    const latestRevisionIdRef = useRef(0);
    //之所以叫Signal而没干脆就是Revison是为以后或许需要扩展，不仅仅只是revision。
    // 现在已经是个(URL, revision)的tuple了，所以有些地方为了避免混淆为rev，所以故意叫revt也可以。FIXME:url是否应该放入revision里面？去掉了表的timestamp字段。url暂时还是保持为同revision形成tuple在前后端间通讯，暂时懒得改了，或许以后这种写法会有其他作用。
    // 修改后的 handleTradingSignals：合并数据后更新 latestRevisionId，并只保留最新 unitCount 个 trading unit
    const handleTradingSignals = useCallback((revts) => {

        // 本来是为了解决前端TradingUnitStreamContainer状态变化显示不更新的问题。并非是我对useState理解有误，发现更深的问题。
        // 原来是老的rev每次都完全不更新的，所以其他前端是拿不到后端新值的。tu和revision层面的数据修改都是实时更新到后端的所以以
        // 后端为准，而order层面的数据不是实时给到后端而是批量一起随着状态从prepare到pending提交的，所以得以前端为准，不能冲了用户前端修改。
        setTradingUnits(prevUnits => {
            logWSRetries.current = 0;

            let newUnits = [...prevUnits];

            revts.forEach(revt => {
                const url = revt[0];
                const revFromBackend = revt[1];
                const tuid = revFromBackend.TUID;

                const unitIndex = newUnits.findIndex(unit => unit[0].TUID === tuid);

                const makeFinalRev = (backendRev, localRev = null) => {
                    //const preservedOrder = localRev?.orders || backendRev.order;
                    const preservedOrders = localRev && localRev.orders ? localRev.orders : backendRev.orders;
                    return {
                        ...backendRev,          // 后端为主
                        URL: url,               // 赋值 URL
                        orders: preservedOrders, // 本地保留 order
                        position: localRev && localRev.hasOwnProperty("position") ? localRev["position"] : backendRev["position"],
                        diff: localRev && localRev.hasOwnProperty("diff") ? localRev["diff"] : backendRev["diff"],
                    };
                };

                if ('init' in revFromBackend) {// head revision
                    // if( [
                    //         "4840753c11e4f2e8",
                    //         "7607e1afa3602ae9",
                    //         "5f217388d03e5dde",
                    //         "00075c88f551dbdc",
                    //     ].includes(tuid)) {
                    //     console.log("[handleTradingSignals] +++ head rev: %s, id %d", tuid, revFromBackend.id);
                    //     //debugger;
                    // }
                    if (unitIndex !== -1) {// tu已存在
                        const oldUnit = newUnits[unitIndex];
                        const localHead = oldUnit[0];
                        const preservedTail = oldUnit.slice(1).map(r => ({ ...r }));
                        const newHead = makeFinalRev(revFromBackend, localHead);
                        newUnits[unitIndex] = [newHead, ...preservedTail];
                    } else {// tu不存在
                        playSoundMultiple(3);//先播放一轮提醒
                        startPlayingSoundMultiple();//设置持续提醒
                        const newUnit = [makeFinalRev(revFromBackend)];
                        newUnit[0].isNew = true; //方便作为新rev显示浮动泡泡
                        newUnits = [newUnit, ...newUnits];
                    }
                } else {  // 普通 revision
                    // if( [
                    //     "4840753c11e4f2e8",
                    //     "7607e1afa3602ae9",
                    //     "5f217388d03e5dde",
                    //     "00075c88f551dbdc",
                    // ].includes(tuid)) {
                    //     console.log("[handleTradingSignals] --- normal rev: %s, id %d", tuid, revFromBackend.id);
                    //     //debugger;
                    // }
                    if (unitIndex !== -1) { // tu已存在
                        const unit = newUnits[unitIndex];
                        const revIndex = unit.findIndex(r => r.id === revFromBackend.id);

                        if (revIndex !== -1) { // rev已存在
                            // 存在，则更新它（以服务器数据为主，保留本地 order）
                            const updated = makeFinalRev(revFromBackend, unit[revIndex]);
                            const updatedUnit = [...unit];
                            updatedUnit[revIndex] = updated;
                            newUnits[unitIndex] = updatedUnit;
                        } else { // rev不存在，追加
                            playSoundMultiple(3);//先播放一轮提醒
                            startPlayingSoundMultiple();//设置持续提醒
                            const newRev = makeFinalRev(revFromBackend);
                            newRev.isNew = true; //方便作为新rev显示浮动泡泡
                            newUnits[unitIndex] = [...unit, newRev];
                        }
                    }
                    // else {
                    //     console.error("[handleTradingSignals] Trading unit<%s> not found for revision:%d", revFromBackend.TUID, revFromBackend.id);
                    // }                    
                }
            });
            forceChildRender();
            return newUnits.slice(0, unitCountRef.current);
        });

        // 更新 latestRevisionId：取当前返回的所有 revts 中最大的 revision.id
        const maxId = Math.max(...revts.map(revt => revt[1].id));
        if (maxId > latestRevisionIdRef.current) {
            latestRevisionIdRef.current = maxId;
        }
    }, [playSoundMultiple, startPlayingSoundMultiple, logWSRetries, forceChildRender]);

    //========================================================================
    // handleUnitCountChange：用户改变条目数量时的逻辑
    const handleUnitCountChange = async (count) => {//没有被别人依赖，所以useCallback可以省略
        //playSoundMultiple(3);
        //startPlayingSoundMultiple()
        playSound();
        blockPage();
        count = Math.min(parseInt(count) || 20, 1000); // 限制最大1000
        setUnitCount(count); // 更新条目数量
        unitCountRef.current = count;//与setStatexxxx同步更新
        // 当 unitCount 变化时，重新全量拉取最新数据（将 latestRevisionId 重置为 0）
        latestRevisionIdRef.current = 0;
        await beFetchTradingUnits(count);
        unblockPage();
    };
    // 提取的 beFetchTradingUnits 函数，用于获取交易单位，TODO：支持 latestRevisionId 机制。
    // beFetchTradingUnits利用的count本来就是外面传进来的，外部传参数不会到导致beFetchTradingUnits重建，没有必要像改成
    // 在函数内部通过unitCountRef.current来获得。函数的设计尽量靠外部传参而不是自己内部读类似全局变量的东西是更好的习惯。
    const beFetchTradingUnits = useCallback(async (count, retries = RETRYTIMES, didRefresh = false) => {
        if (!isLoggedInRef.current)//必须用新值，但没必要使用isLoggedIn作为依赖而导致本useCallback套函数重建，反正current值会与isLoggedIn同步更新。
            return; // 避免未登录时拉取数据
        try {
            // // 判断是否需要全量拉取：当当前存储的 tradingUnits 数量不足 unitCount，则全量拉取（latestRevisionId = -1），否则只拉取新增数据
            // const needFullFetch = tradingUnits.length < count;
            // const currentLatestRevisionId = needFullFetch ? -1 : latestRevisionIdRef.current;  机制暂时有问题，先不用。固定为-1
            const currentLatestRevisionId = -1;//FIXME: 强制全量模式拉取
            const url = `${ServiceURLPrefix}/user/tr/gets?offset=-1&limit=${count}&latestRevisionId=${currentLatestRevisionId}`;
            const response = await ReliableFetch(url, {
                method: "GET",
                credentials: 'include' // 带cookie的关键！
            });
            if (!response.ok || response.status === 204) {
                console.error(`[beFetchTradingUnits]: No signals available (status: ${response.status})`);
                if (response.status === 401 || response.status === 403) {//没必要重试
                    // FIXME(iOS-auth): iOS 跨站 Cookie 可能导致偶发 401/403（尤其是带 Authorization 会触发预检/链路更复杂）。
                    // 这里先尝试一次 refresh + retry，仍失败再登出，避免“登录后立刻被打回登录页”。
                    if (isIOSBrowser() && !didRefresh) {
                        console.warn("[beFetchTradingUnits] iOS got 401/403, try refresh once then retry...");
                        setMessage(`[WARN]beFetchTradingUnits: iOS 收到 ${response.status}，尝试 refresh 后重试一次...`);
                        const refreshed = await beRefreshToken(0);
                        if (refreshed) {
                            return beFetchTradingUnits(count, retries, true);
                        }
                    }
                    console.error("[beFetchTradingUnits] Token expired or unauthenticated, logout...");
                    setMessage(`[ERROR]beFetchTradingUnits: ${response.status} 未认证，触发登出（didRefresh=${didRefresh}）`);
                    recordLogoutReason(`beFetchTradingUnits ${response.status} didRefresh=${didRefresh} url=${url}`);
                    handleLogout();
                    return false;
                }
                if (retries > 0) {
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    return beFetchTradingUnits(count, retries - 1, didRefresh);
                }
                return false;
            }
            const revts = await response.json();//(URL, revison)
            //console.info(signals)
            if (!revts?.length) {//后端返回的是空数组，或者None都会到这里
                console.error(`[beFetchTradingUnits]: signals is null or empty! Why?response: ${response}`);
                return false;
            }
            handleTradingSignals(revts);
            return true;
        } catch (error) {
            console.error("[FATAL]beFetchTradingUnits: Error fetching signals:", error.message, error.stack);
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 5000));
                return beFetchTradingUnits(count, retries - 1, didRefresh);
            }
            handleLogout();
            console.error("[FATAL]beFetchTradingUnits: Error fetching signals:", error.message, error.stack);
            return false;
        }
        // FIXME：beFetchTradingUnits依赖一个自己会导致变化的变量tradingUnits本身就是有问题的。导致eslint总是提示需要此依赖的原因是上面的
        // const needFullFetch = tradingUnits.length < count;这个语句，已经被注释了。以后如果要启用优化抓取的话这个语句也得改进，避免循环依赖。
        // 必须加上isLoggedIn依赖项，否则当isLoggedIn变化的时候fetchTradingUnits箭头函数不会重新赋值，这样就一直是旧值。
        // # 同AI相关的讨论：虽然函数本身没有状态，但它会捕获其所在闭包中的变量值。如果把 isLoggedIn 从依赖中移除，beFetchTradingUnits 函数会“记住”初始化时的 isLoggedIn 值，而不会随着后续 isLoggedIn 的变化而更新。这样可能会导致以下问题：
        // 1 闭包中的旧值（stale closure）：即使 isLoggedIn 后来变了，函数内部仍然使用初始的旧值。例如，如果 isLoggedIn 从 true 变为 false，但 beFetchTradingUnits 没有更新，那么它依然认为用户已登录，从而执行错误的逻辑。
        // 2 逻辑错误：如果函数内部依赖 isLoggedIn 来判断是否拉取数据，使用旧值可能导致数据拉取不符合当前的登录状态，出现潜在安全或逻辑错误。
        // 总结来说，虽然函数是无状态的，但它依赖于其闭包中的变量。如果这些依赖未在 useCallback 的依赖数组中更新，闭包中的值就会“冻结”，从而可能导致 bug
    }, [ServiceURLPrefix, handleLogout, handleTradingSignals, beRefreshToken, setMessage, recordLogoutReason]);
    // 定时任务：从后端拉取一批交易信号
    useEffect(() => {
        if (!isLoggedIn) {
            console.info("Not logged in.");
            clearUserData();  // 调用清理函数
            return;
        }
        fetchTradingUnitsIntervalRef.current = setInterval(async () => {
            beFetchTradingUnits(unitCountRef.current);//必须用新值，但没必要使用unitCount作为依赖而导致本函数重建，反正current值会与unitCount同步更新。
            //}, 10*1000);
        }, FETCH_TRADING_UNITS_INTERVAL_MS);
        //}, 10*12*5000);
        //}, 600000);
        return () => {
            // 清除交易数据拉取定时器
            if (fetchTradingUnitsIntervalRef.current) {
                clearInterval(fetchTradingUnitsIntervalRef.current);
            }
        };
        // 内部代码看似用到了beFetchTradingUnits，理论上应该加它为依赖。但是beFetchTradingUnits虽是useCallback，但其依赖众多,特别是依赖tradingUnits，
        // 会导致beFetchTradingUnits频繁变化，而导致本useEffect频繁执行，而影响定时器的意图，定时器又影响抓取tradingUnits，导致循环调用。
        // 不过beFetchTradingUnits依赖一个自己会导致变化的变量tradingUnits本身就是有问题的。导致eslint总是提示需要此依赖的原因是
        // const needFullFetch = tradingUnits.length < count;这个语句，已经被注释了。以后如果要启用优化抓取的话这个语句也得改进，避免循环依赖。
        // 而至于clearUserData' and 'setMessage 函数不会变，不想作为依赖了，也懒得定义那么多useCallback了，每个useCallback又得指定依赖。
        //}, [isLoggedIn, unitCount]); //beFetchTradingUnits还是不能加，会导致反复尝试/auth/status，把浏览器都搞死了。不知道为什么
    }, [isLoggedIn, beFetchTradingUnits, clearUserData, FETCH_TRADING_UNITS_INTERVAL_MS]);//useState范围的setter函数都是稳定的，不怕放在依赖里

    //========================================================================
    //FIXME：正式要打开。选择服务器是调试阶段的需要，后面可以把选择服务URL和删除cookie（HttpOnly的前端没法删其实）强制必须登录注释掉，恢复useEffect的/auth/status检测。
    // 第一次检测一下登录状态，好确定应该显示什么界面，以及通过setIsLoggedIn把isLoggedIn从null初始化初值明确现值
    useEffect(() => {
        (async () => {
            try {
                try {
                    const last = sessionStorage.getItem("last_logout_reason");
                    if (last) {
                        if (IS_IOS) setLogoutReason(`上次被动退出原因: ${last}`);
                        sessionStorage.removeItem("last_logout_reason");
                    }
                } catch {
                    // ignore
                }
                const response = await ReliableFetch(`${ServiceURLPrefix}/auth/status`, {
                    method: 'GET',
                    credentials: 'include'
                });
                const data = await response.json();
                if (response.ok) {
                    const loggedIn = data.status === "authenticated";
                    // 这里只用于“首次初始化检查”。若用户已主动完成登录/登出（isLoggedInRef 不为 null），
                    // 则不要让这个异步返回覆盖当前状态（避免登录后被旧的 unauthenticated 结果打回登录页）。
                    // FIXME(iOS-auth): iOS 跨站 Cookie 可能导致首次 status 返回 unauthenticated；应优先用用户刚完成的登录结果推进状态。
                    if (isLoggedInRef.current === null) {
                        setIsLoggedIn(loggedIn);
                        isLoggedInRef.current = loggedIn;//与setStatexxxx同步更新
                    }
                    console.info(`[useEffect.checkAuth]: ${data.status}: ${data.message}`);
                    setMessage(`[INFO]useEffect.checkAuth: ${data.status}: ${data.message}`);
                    if (!loggedIn) {
                        startPlayingSoundMultiple();
                    } else {
                        stopPlaying();
                    }
                } else {
                    if (isLoggedInRef.current === null) {
                        setIsLoggedIn(false);
                        isLoggedInRef.current = false;//与setStatexxxx同步更新
                    }
                    console.error(`[useEffect.checkAuth]: ${data.message}`);
                    setMessage(`[ERROR]useEffect.checkAuth: ${data.message}`);
                    startPlayingSoundMultiple();
                }
            } catch (error) {
                if (isLoggedInRef.current === null) {
                    setIsLoggedIn(false);
                    isLoggedInRef.current = false;//与setStatexxxx同步更新
                }
                console.error(`[FATAL]useEffect.checkAuth: ${error}`);
                setMessage(`[FATAL]useEffect.checkAuth: ${error}`);
            }
        })();
    }, [ServiceURLPrefix, startPlayingSoundMultiple, setMessage, IS_IOS]);//useState范围的setter函数都是稳定的，不怕放在依赖里
    //}, [isLoggedIn]);
    // //FIXME：临时函数，第一次执行清除登录。选择服务器是调试阶段的需要，后面可以把选择服务URL和删除cookie（HttpOnly的前端没法删其实）强制必须登录注释掉，恢复useEffect的/auth/status检测。
    // useEffect(() => {
    //     setIsLoggedIn(false); // 重新设置登录状态为 false
    //     isLoggedInRef.current= false;//与setStatexxxx同步更新
    //     clearUserData()
    //     console.debug(" ... clear ...");
    // }, []);

    //========================================================================
    return (
        <div className="MainApp">
            <div><LogMonitor /></div>
            {IS_IOS && logoutReason && <p>{logoutReason}</p>}
            {message && <p>{message}</p>}
            {isLoggedIn === null ? ( // 根据 isLoggedIn 的状态显示不同的内容
                <div>正在检查登录状态...</div> // 或 loading 动画
            ) : !isLoggedIn ? (
                <div className="auth-container">
                    {authMode === "login" ? (
                        <div>
                            <Login onLogin={handleLogin} />
                            <p>
                                还没有账号？{" "}
                                <button onClick={() => {
                                    setAuthMode("register");
                                    playSound();
                                }}>
                                    注册
                                </button>
                            </p>
                        </div>
                    ) : (
                        <div>
                            <Register onRegister={handleRegister} />
                            <p>
                                已有账号？{" "}
                                <button onClick={() => {
                                    setAuthMode("login");
                                    playSound();
                                }}>
                                    登录
                                </button>
                            </p>
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    <span style={{ fontSize: '10px', color: 'mediumblue', fontWeight: 'bold' }}>
                        &nbsp;&nbsp;{username}&nbsp;&nbsp;
                    </span>
                    <span style={{ fontSize: '12px', color: 'teal', fontWeight: 'bold' }}>
                        「 {ServerName} 」&nbsp;&nbsp;
                    </span>
                    <button onClick={handleLogout}>{t("Logout")}</button>

                    <span
                        onDoubleClick={() => {
                            setLocalTimezone(localTimezone === 'UTC' ? 'Asia/Singapore' : 'UTC');
                            playSound();
                        }}
                        className="editable-field" style={{ fontSize: '12px', color: 'fuchsia', fontWeight: 'bold' }}
                    >
                        &nbsp;&nbsp;{t("Timezone")}: {localTimezone}
                    </span>

                    <div className="count-controls">
                        {UNIT_COUNT_PRESETS.map(n => (
                            <button
                                key={n}
                                onClick={() => handleUnitCountChange(n)}
                                className={unitCount === n ? 'active' : ''}
                            >
                                最新{n}个
                            </button>
                        ))}
                        <div className="custom-count">
                            <input
                                type="number"
                                min="1"
                                max="1000"
                                placeholder="自定义"
                                onChange={(e) => handleUnitCountChange(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="trading-units-container">
                        {
                            tradingUnits.map(tradingUnit => (
                                <TradingUnitStreamContainer
                                    key={tradingUnit[0].TUID}
                                    tradingUnit={tradingUnit}
                                    forceRenderTrigger={forceRenderCounter}
                                />
                            ))
                        }
                    </div>

                </div>
            )}
            {isBlocking && <div className="blocking-overlay"></div>}
        </div>
    );
};

//============================================================================
export default App;
